import { useState } from 'react';

export default function LessonFormModal({ lesson, isNew, onSave, onClose }) {
  const [draft, setDraft] = useState({
    id: lesson.id || `lesson-${Date.now()}`,
    title: lesson.title || '',
    type: lesson.type || 'video',
    videoUrl: lesson.videoUrl || '',
    textContent: lesson.textContent || '',
    slides: lesson.slides || [],
    duration: lesson.duration || '',
    isPreview: lesson.isPreview || false,
    quiz: lesson.quiz || { title: 'Lesson Quiz', questions: [] }
  });

  // Store actual File objects for upload
  const [slideFiles, setSlideFiles] = useState({}); // { slideId: File }

  // Local notification for form-level errors
  const [notification, setNotification] = useState({
    show: false,
    type: '',
    message: ''
  });

  const showFormNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => {
      setNotification({ show: false, type: '', message: '' });
    }, 4000);
  };

  const updateDraft = (updates) => {
    setDraft(prev => ({ ...prev, ...updates }));
  };

  const handleTypeChange = (newType) => {
    updateDraft({ type: newType });
  };

  // Add new slide
  const addSlide = () => {
    const newSlide = {
      id: `slide-${Date.now()}`,
      imageUrl: '',        // Will be replaced by uploaded URL from backend
      caption: '',
      order: draft.slides.length,
    };
    updateDraft({ slides: [...draft.slides, newSlide] });
  };

  // Handle image file selection for a slide
  const handleSlideImageSelect = (index, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const slideId = draft.slides[index].id;
    
    // Store the actual File
    setSlideFiles(prev => ({ ...prev, [slideId]: file }));

    // Create preview URL for immediate feedback
    const previewUrl = URL.createObjectURL(file);
    
    const updatedSlides = [...draft.slides];
    updatedSlides[index] = { 
      ...updatedSlides[index], 
      imageUrl: previewUrl   // temporary preview
    };
    
    updateDraft({ slides: updatedSlides });
  };

  const updateSlide = (index, field, value) => {
    const updatedSlides = [...draft.slides];
    updatedSlides[index] = { ...updatedSlides[index], [field]: value };
    updateDraft({ slides: updatedSlides });
  };

  const removeSlide = (index) => {
    const slideId = draft.slides[index].id;
    // Clean up file if exists
    setSlideFiles(prev => {
      const newFiles = { ...prev };
      delete newFiles[slideId];
      return newFiles;
    });

    updateDraft({
      slides: draft.slides.filter((_, i) => i !== index)
    });
  };

  const handleSave = () => {
    if (!draft.title.trim()) {
      showFormNotification('error', 'Lesson title is required');
      return;
    }

    // Pass both lesson data and slide files to parent
    const finalLesson = {
      ...draft,
      videoUrl: draft.type === 'video' ? draft.videoUrl : undefined,
      textContent: draft.type === 'text' ? draft.textContent : undefined,
      slides: draft.type === 'slides' ? draft.slides : undefined,
    };

    // Pass slide files separately so parent can build FormData
    onSave(finalLesson, slideFiles);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden">
        
        <div className="flex items-center justify-between p-6 border-b border-slate-100 flex-shrink-0">
          <h2 className="text-xl font-bold text-slate-900">
            {isNew ? 'Add New Lesson' : 'Edit Lesson'}
          </h2>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-2xl">
            <i className="ri-close-line text-2xl" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Form Notification */}
          {notification.show && (
            <div className={`px-4 py-3 rounded-2xl border flex items-center gap-3 text-sm font-medium ${
              notification.type === 'error' 
                ? 'bg-rose-50 border-rose-200 text-rose-700' 
                : 'bg-emerald-50 border-emerald-200 text-emerald-700'
            }`}>
              <i className={`ri-${notification.type === 'error' ? 'error-warning' : 'check'}-line text-lg`} />
              {notification.message}
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Lesson Title *</label>
            <input
              type="text"
              value={draft.title}
              onChange={(e) => updateDraft({ title: e.target.value })}
              placeholder="e.g. Understanding React Hooks"
              className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:outline-none focus:border-teal-500"
            />
          </div>

          {/* Content Type */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Lesson Type</label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'video', label: 'Video', icon: 'ri-play-circle-fill' },
                { value: 'slides', label: 'Slides', icon: 'ri-slideshow-3-line' },
                { value: 'text', label: 'Text Only', icon: 'ri-article-line' },
              ].map((t) => (
                <button
                  key={t.value}
                  onClick={() => handleTypeChange(t.value)}
                  className={`flex flex-col items-center gap-2 py-4 rounded-2xl border transition-all ${
                    draft.type === t.value
                      ? 'border-teal-600 bg-teal-50 text-teal-700'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <i className={`${t.icon} text-2xl`} />
                  <span className="text-sm font-medium">{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Video */}
          {draft.type === 'video' && (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Video URL</label>
              <input
                type="text"
                value={draft.videoUrl}
                onChange={(e) => updateDraft({ videoUrl: e.target.value })}
                placeholder="https://www.youtube.com/embed/..."
                className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:outline-none focus:border-teal-500"
              />
            </div>
          )}

          {/* Text Content */}
          {draft.type === 'text' && (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Lesson Content</label>
              <textarea
                value={draft.textContent}
                onChange={(e) => updateDraft({ textContent: e.target.value })}
                rows={12}
                placeholder="Write your lesson content here..."
                className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:outline-none focus:border-teal-500 resize-y min-h-[280px]"
              />
            </div>
          )}

          {/* Slides with Real File Upload */}
          {draft.type === 'slides' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <label className="text-sm font-semibold text-slate-700">Slides ({draft.slides.length})</label>
                <button
                  onClick={addSlide}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-2xl hover:bg-teal-700"
                >
                  <i className="ri-add-line" /> Add Slide
                </button>
              </div>

              <div className="space-y-4">
                {draft.slides.map((slide, index) => (
                  <div key={slide.id} className="border border-slate-200 rounded-2xl p-5 bg-slate-50">
                    <div className="flex justify-between mb-4">
                      <span className="font-medium text-slate-600">Slide {index + 1}</span>
                      <button
                        onClick={() => removeSlide(index)}
                        className="text-rose-500 hover:text-rose-600"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>

                    <input
                      type="text"
                      placeholder="Slide caption (optional)"
                      value={slide.caption || ''}
                      onChange={(e) => updateSlide(index, 'caption', e.target.value)}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl mb-3"
                    />

                    {/* File Upload for Slide Image */}
                    <div className="mb-3">
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">Slide Image</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleSlideImageSelect(index, e)}
                        className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                      />
                    </div>

                    {/* Preview */}
                    {slide.imageUrl && (
                      <div className="mt-3 rounded-xl overflow-hidden border border-slate-200">
                        <img 
                          src={slide.imageUrl || `http://localhost:30022/${slide.imageUrl}`} 
                          alt="slide preview" 
                          className="w-full h-48 object-cover" 
                        />
                      </div>
                    )}
                  </div>
                ))}

                {draft.slides.length === 0 && (
                  <div className="text-center py-12 border border-dashed border-slate-300 rounded-2xl">
                    <p className="text-slate-400">No slides added yet. Click "Add Slide" above.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Lesson Quiz (Optional)</label>
            <button
              onClick={() => {
                // We'll open quiz editor from parent for now
                onSave({ ...draft, quiz: draft.quiz }, slideFiles);
              }}
              className="w-full py-3 border border-teal-200 text-teal-700 rounded-2xl hover:bg-teal-50 flex items-center justify-center gap-2"
            >
              <i className="ri-question-answer-line" />
              {draft.quiz?.questions?.length || 0} Questions - Edit Quiz
            </button>
          </div>
        </div>

        <div className="border-t border-slate-100 p-6 flex justify-end gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-3 border border-slate-200 rounded-2xl font-medium text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!draft.title.trim()}
            className="px-8 py-3 bg-teal-600 text-white font-semibold rounded-2xl hover:bg-teal-700 disabled:opacity-50 transition-colors"
          >
            {isNew ? 'Add Lesson' : 'Save Lesson'}
          </button>
        </div>
      </div>
    </div>
  );
}